'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Button, LinkButton } from '@/components/ui/Button/Button';
import { analytics } from '@/lib/analytics';

/**
 * Route-segment error boundary for everything under (main) — including the
 * plugin-webview select flow. Renders inside the root layout, so the navbar
 * and theme survive and users (especially webview users, who have no browser
 * chrome) can retry or navigate instead of hitting Next's bare default screen.
 * Errors escalate to app/global-error.tsx only if the layout itself throws.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    analytics.captureException(error, { boundary: 'app-error', digest: error.digest });
  }, [error]);

  const t = useTranslations('errorPage');

  return (
    <div className="min-h-[80vh] flex items-center justify-center flex-col gap-4 px-4 text-center">
      {/* Offset tri-color layers, same treatment as the 404 page's glyph. */}
      <h1
        className="text-4xl sm:text-5xl font-bold uppercase"
        style={{ textShadow: '3px 0 #FF0000, -3px 0 #0000FF, 0 3px #FFFF00' }}
      >
        {t('title')}
      </h1>
      <p className="text-muted-foreground">{t('description')}</p>
      {error.digest && (
        <p className="text-xs text-zinc-500 font-mono">{t('errorCode', { digest: error.digest })}</p>
      )}
      <div className="flex flex-row gap-4 mt-2">
        <Button isPrimary onClick={reset}>
          {t('tryAgain')}
        </Button>
        <LinkButton href="/">{t('returnHome')}</LinkButton>
      </div>
    </div>
  );
}
